import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Copy as CopyIcon, HardDrive, Loader2, Sparkles, Tag as TagIcon, Upload } from "lucide-react";
import { apiClient } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PrintCardsSection from "@/components/PrintCardsSection";
import AdminCspLink from "@/components/AdminCspLink";
import UnclassifiedLifeCycleSection from "@/components/UnclassifiedLifeCycleSection";
import { SettingsLink } from "./primitives";
import { useBatchedBackfill } from "./useBatchedBackfill";

/**
 * Backfill labels hoisted to module scope so their object identity is
 * stable across renders — otherwise the `useBatchedBackfill` `run`
 * callback would rebuild every render and defeat its own memoization.
 */
const USES_LABELS = {
  starting: "settings.fillingUsesLabel",
  progress: "settings.fillUsesProgress",
  retrying: "settings.fillUsesRetrying",
  partial: "settings.fillUsesPartial",
  success: "settings.fillUsesSuccess",
};

const LIFE_CYCLE_LABELS = {
  starting: "settings.fillingLifeCycleLabel",
  progress: "settings.fillLifeCycleProgress",
  retrying: "settings.fillLifeCycleRetrying",
  partial: "settings.fillLifeCyclePartial",
  success: "settings.fillLifeCycleSuccess",
};

const DORMANCY_LABELS = {
  starting: "settings.fillingDormancyLabel",
  progress: "settings.fillDormancyProgress",
  retrying: "settings.fillDormancyRetrying",
  partial: "settings.fillDormancyPartial",
  success: "settings.fillDormancySuccess",
};

/**
 * MaintenanceSection — housekeeping actions the user can run against
 * their own library:
 *
 * • Info links: find untagged, find near-duplicates, print cards.
 * • Restore from CSV: user picks a `.csv` backup → confirm dialog →
 *   POST /plants/restore-from-csv. Client-side sniff on the header
 *   line so we don't send random files to the server.
 * • Compact storage: recompresses already-stored photos to match the
 *   byte budget new uploads enforce. Batched in ~8-scan chunks to stay
 *   under Cloudflare's ~100s edge timeout.
 * • Three text-only Gemini backfills: uses, life-cycle, dormancy. Each
 *   uses the shared `useBatchedBackfill` hook.
 *
 * Also renders the "review unclassified life-cycle plants" section
 * below the card (component self-hides when the count is 0).
 */
export default function MaintenanceSection() {
  const { t } = useTranslation();

  const [restoring, setRestoring] = useState(false);
  const [pendingRestore, setPendingRestore] = useState(null); // { csv, filename }
  const [restorePreview, setRestorePreview] = useState(null); // { estimatedRows }

  // Storage compaction — a bespoke loop (different response shape:
  // bytes_saved, processed) so it stays inline instead of using the
  // hook.
  const [compactStatus, setCompactStatus] = useState(null);
  const [compacting, setCompacting] = useState(false);

  const loadCompactStatus = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/plants/compress-existing/status");
      setCompactStatus(data);
    } catch {
      setCompactStatus(null);
    }
  }, []);

  const runCompact = async () => {
    setCompacting(true);
    let totalSaved = 0;
    let totalProcessed = 0;
    const toastId = toast.loading(t("settings.compactingLabel"));
    try {
      // Hard cap on iterations to defend against an infinite loop
      // (should never happen because `remaining` decreases each call).
      for (let i = 0; i < 200; i++) {
        const { data } = await apiClient.post(
          "/plants/compress-existing?batch=8",
          {},
          { timeout: 90 * 1000 } // < Cloudflare's 100s edge cutoff
        );
        totalProcessed += data.processed || 0;
        totalSaved += data.bytes_saved || 0;
        const savedMb = Math.max(0, totalSaved / (1024 * 1024)).toFixed(1);
        toast.loading(
          t("settings.compactProgress", {
            n: totalProcessed,
            mb: savedMb,
            remaining: data.remaining,
          }),
          { id: toastId }
        );
        // Stop when we made no progress OR nothing's left.
        if (!data.processed || !data.remaining) break;
      }
      const savedMb = (totalSaved / (1024 * 1024)).toFixed(2);
      toast.success(t("settings.compactSuccess", { n: totalProcessed, mb: savedMb }), { id: toastId });
      await loadCompactStatus();
    } catch {
      toast.error(t("settings.compactFail"), { id: toastId });
    } finally {
      setCompacting(false);
    }
  };

  // Three near-identical text-only Gemini backfills sharing one hook.
  const usesBackfill = useBatchedBackfill({
    statusUrl: "/plants/backfill-uses/status",
    runUrl: "/plants/backfill-uses?batch=2",
    labels: USES_LABELS,
  });

  const lifeCycleBackfill = useBatchedBackfill({
    statusUrl: "/plants/backfill-life-cycle/status",
    runUrl: "/plants/backfill-life-cycle?batch=3",
    labels: LIFE_CYCLE_LABELS,
  });

  const dormancyBackfill = useBatchedBackfill({
    statusUrl: "/plants/backfill-dormancy/status",
    runUrl: "/plants/backfill-dormancy?batch=3",
    labels: DORMANCY_LABELS,
  });

  useEffect(() => {
    loadCompactStatus();
    usesBackfill.loadStatus();
    lifeCycleBackfill.loadStatus();
    dormancyBackfill.loadStatus();
    // Loaders are memoized with useCallback so identity is stable.
  }, [loadCompactStatus, usesBackfill.loadStatus, lifeCycleBackfill.loadStatus, dormancyBackfill.loadStatus]);

  const onPickRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the user re-pick the same file later
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t("settings.restoreTooLarge"));
      return;
    }
    let text = "";
    try {
      text = await file.text();
    } catch {
      toast.error(t("settings.restoreReadFail"));
      return;
    }
    // Fast client-side sniff so we don't hit the server with random files.
    if (!text.startsWith("# Prairie plant backup")) {
      toast.error(t("settings.restoreNotOurs"));
      return;
    }
    // Rough row-count preview (comment lines + header + N data rows).
    const rowCount = Math.max(0, text.split("\n").filter((l) => l && !l.startsWith("#")).length - 1);
    setPendingRestore({ csv: text, filename: file.name });
    setRestorePreview({ estimatedRows: rowCount });
  };

  const confirmRestore = async () => {
    if (!pendingRestore) return;
    setRestoring(true);
    setPendingRestore(null);
    try {
      const { data } = await apiClient.post("/plants/restore-from-csv", { csv: pendingRestore.csv });
      toast.success(t("settings.restoreSuccess", { created: data.created, skipped: data.skipped }));
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const key = detail === "not_a_prairie_backup" || detail === "missing_signature" || detail === "invalid_signature"
        ? `settings.restore_${detail}`
        : "settings.restoreFail";
      toast.error(t(key));
    } finally {
      setRestoring(false);
      setRestorePreview(null);
    }
  };

  const hasCompact = compactStatus && compactStatus.total > 0;
  const compactPending = compactStatus?.pending ?? 0;

  return (
    <>
      <div className="bg-white rounded-3xl border border-[#E8E5DF] divide-y divide-[#E8E5DF] overflow-hidden">
        <SettingsLink to="/gallery?filter=untagged" icon={<TagIcon className="w-4 h-4" strokeWidth={1.5} />} testId="find-untagged-link">
          {t("settings.findUntagged")}
        </SettingsLink>
        <SettingsLink to="/duplicates?mode=species" icon={<CopyIcon className="w-4 h-4" strokeWidth={1.5} />} testId="find-near-dupes-link">
          {t("settings.findNearDupes")}
        </SettingsLink>
        {/* Owner-only: printable info-card generator. Component renders
            null for non-owners so nothing leaks to other users. */}
        <PrintCardsSection />

        {/* Owner-only: CSP violation dashboard. Same "renders nothing for
            non-owners" pattern as PrintCardsSection above. */}
        <AdminCspLink />

        {/* Restore from CSV — file picker styled as a settings row. */}
        <label
          data-testid="restore-csv-label"
          className={`w-full flex items-center gap-3 px-5 py-4 hover:bg-parchment transition text-leaf-ink text-left cursor-pointer ${
            restoring ? "opacity-60 pointer-events-none" : ""
          }`}
        >
          <span className="text-leaf">
            {restoring
              ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              : <Upload className="w-4 h-4" strokeWidth={1.5} />}
          </span>
          <span className="flex-1 flex flex-col">
            <span>{restoring ? t("settings.restoring") : t("settings.restoreCsv")}</span>
            <span className="text-[11px] text-leaf-mist mt-0.5">{t("settings.restoreCsvHint")}</span>
          </span>
          <span className="text-leaf-mist">›</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={onPickRestoreFile}
            data-testid="restore-csv-input"
            className="hidden"
          />
        </label>

        {/* Compact storage — always visible when the user has any scans,
            but disabled + "everything's compact" copy when there's
            nothing pending. Rendering unconditionally makes it
            discoverable after a successful run without a dead-end button. */}
        {hasCompact && (
          <button
            type="button"
            onClick={runCompact}
            disabled={compacting || compactPending === 0}
            data-testid="compact-storage-button"
            className="w-full flex items-center gap-3 px-5 py-4 hover:bg-parchment disabled:hover:bg-transparent disabled:opacity-70 disabled:cursor-default transition text-leaf-ink text-left"
          >
            <span className="text-leaf">
              {compacting
                ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                : <HardDrive className="w-4 h-4" strokeWidth={1.5} />}
            </span>
            <span className="flex-1 flex flex-col">
              <span>
                {compacting
                  ? t("settings.compactingLabel")
                  : compactPending === 0
                    ? t("settings.compactDoneLabel")
                    : t("settings.compactLabel")}
              </span>
              <span className="text-[11px] text-leaf-mist mt-0.5">
                {compactPending === 0
                  ? t("settings.compactDoneHint")
                  : t("settings.compactHint", { n: compactPending })}
              </span>
            </span>
            {compactPending > 0 && <span className="text-leaf-mist">›</span>}
          </button>
        )}

        {/* Storage-used line — persistent info. Doesn't render at all if
            we couldn't fetch the status (network error, brand-new user). */}
        {hasCompact && (
          <div
            data-testid="storage-used-line"
            className="w-full flex items-center gap-3 px-5 py-3 text-leaf-mist text-[11px] uppercase tracking-[0.16em] border-t border-[#EFEBE3]"
          >
            <HardDrive className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span>{t("settings.storageUsedLabel")}</span>
            <span className="ml-auto text-leaf-ink normal-case tracking-normal text-sm [font-variant-numeric:lining-nums_tabular-nums]">
              {t("settings.storageUsedValue", {
                mb: compactStatus.human_used_mb ?? 0,
                n: compactStatus.total,
              })}
            </span>
          </div>
        )}

        {/* Three backfills — each renders only when there's work to do
            so long-time users don't see permanently-disabled rows. */}
        <BackfillRow
          testId="fill-uses-button"
          pending={usesBackfill.status?.pending}
          running={usesBackfill.running}
          onRun={usesBackfill.run}
          runLabel={t("settings.fillUsesLabel")}
          runningLabel={t("settings.fillingUsesLabel")}
          hintKey="settings.fillUsesHint"
          n={usesBackfill.status?.pending}
        />
        <BackfillRow
          testId="fill-life-cycle-button"
          pending={lifeCycleBackfill.status?.pending}
          running={lifeCycleBackfill.running}
          onRun={lifeCycleBackfill.run}
          runLabel={t("settings.fillLifeCycleLabel")}
          runningLabel={t("settings.fillingLifeCycleLabel")}
          hintKey="settings.fillLifeCycleHint"
          n={lifeCycleBackfill.status?.pending}
        />
        <BackfillRow
          testId="fill-dormancy-button"
          pending={dormancyBackfill.status?.pending}
          running={dormancyBackfill.running}
          onRun={dormancyBackfill.run}
          runLabel={t("settings.fillDormancyLabel")}
          runningLabel={t("settings.fillingDormancyLabel")}
          hintKey="settings.fillDormancyHint"
          n={dormancyBackfill.status?.pending}
        />
      </div>

      {/* Review unclassified life-cycle plants — component self-hides
          when the count is 0 so users with clean data never see it. */}
      <UnclassifiedLifeCycleSection
        status={lifeCycleBackfill.status}
        onStatusChange={lifeCycleBackfill.loadStatus}
      />

      <AlertDialog open={!!pendingRestore} onOpenChange={(o) => { if (!o) { setPendingRestore(null); setRestorePreview(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display" data-testid="restore-confirm-title">
              {t("settings.restoreConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.restoreConfirmDesc", {
                count: restorePreview?.estimatedRows || 0,
                filename: pendingRestore?.filename || "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="restore-confirm"
              onClick={(e) => { e.preventDefault(); confirmRestore(); }}
              className="bg-leaf hover:bg-leaf-deep font-bold uppercase tracking-wide"
            >
              {t("settings.restoreConfirmAction")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * BackfillRow — one of the three "fill missing X" list-rows. Only
 * renders when there's pending work to do (matches the previous
 * behaviour so users with clean data don't see disabled rows).
 */
function BackfillRow({ testId, pending, running, onRun, runLabel, runningLabel, hintKey, n }) {
  const { t } = useTranslation();
  if (!pending || pending === 0) return null;
  return (
    <button
      type="button"
      onClick={onRun}
      disabled={running}
      data-testid={testId}
      className="w-full flex items-center gap-3 px-5 py-4 hover:bg-parchment disabled:hover:bg-transparent disabled:opacity-70 disabled:cursor-default transition text-leaf-ink text-left border-t border-[#EFEBE3]"
    >
      <span className="text-leaf">
        {running
          ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
          : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
      </span>
      <span className="flex-1 flex flex-col">
        <span>{running ? runningLabel : runLabel}</span>
        <span className="text-[11px] text-leaf-mist mt-0.5">{t(hintKey, { n })}</span>
      </span>
      <span className="text-leaf-mist">›</span>
    </button>
  );
}
